import {
  DataInvalidationScope,
  type DataInvalidationScope as Scope,
} from '@gb-ticket/shared-types';

/** A dialog or an unsaved profile form blocks the lists those screens edit. */
export type InvalidationHold = {
  dialogOpen: boolean;
  profileDirty: boolean;
};

/**
 * Apply now, or remember the scope until the hold clears.
 * `dialogOpen` blocks user and organisation lists. An unsaved profile blocks every scope,
 * because that form also edits notification preferences.
 */
export function acceptInvalidation(scope: Scope, hold: InvalidationHold): 'apply' | 'defer' {
  if (hold.profileDirty) {
    return 'defer';
  }
  if (
    hold.dialogOpen &&
    (scope === DataInvalidationScope.USERS || scope === DataInvalidationScope.ORGANISATION)
  ) {
    return 'defer';
  }
  return 'apply';
}

/** React Query keys for one scope. Prefixes match the hooks that own those lists. */
export function queryKeysForInvalidation(scope: Scope): readonly (readonly string[])[] {
  switch (scope) {
    case DataInvalidationScope.NOTIFICATIONS:
      return [['notifications']];
    case DataInvalidationScope.USERS:
      return [['users']];
    case DataInvalidationScope.ORGANISATION:
      return [['departments'], ['sites'], ['teams']];
    case DataInvalidationScope.PRODUCTS:
      return [
        ['products', 'settings'],
        ['auth', 'me'],
      ];
    case DataInvalidationScope.DIRECTORY:
      return [['directory', 'status']];
    case DataInvalidationScope.INTRANET_CHAT:
      return [['intranet', 'chat']];
    case DataInvalidationScope.AI:
      return [['ai', 'connection']];
    case DataInvalidationScope.MAIL:
      return [['mail', 'connection']];
    case DataInvalidationScope.MASTER_DATA:
      return [['master-data']];
    case DataInvalidationScope.ARTICLES:
      return [['articles']];
    default: {
      const exhaustive: never = scope;
      return exhaustive;
    }
  }
}

let hold: InvalidationHold = { dialogOpen: false, profileDirty: false };
const pending = new Set<Scope>();

export function receiveInvalidation(scope: Scope): Scope | null {
  if (acceptInvalidation(scope, hold) === 'defer') {
    pending.add(scope);
    return null;
  }
  return scope;
}

export function setDialogInvalidationHold(open: boolean): Scope[] {
  hold = { ...hold, dialogOpen: open };
  return open ? [] : drainInvalidations();
}

export function setProfileInvalidationHold(dirty: boolean): Scope[] {
  hold = { ...hold, profileDirty: dirty };
  return dirty ? [] : drainInvalidations();
}

function drainInvalidations(): Scope[] {
  const ready: Scope[] = [];
  for (const scope of pending) {
    if (acceptInvalidation(scope, hold) === 'apply') {
      pending.delete(scope);
      ready.push(scope);
    }
  }
  return ready;
}
